import type Request from './Request';

/**
 * @class Pagination
 * @private
 * @desc Class allowing pagination usage on binded request.
 * @property {Number} currentPage Current page.
 * @property {Number} elementsByPage Number of elements by page.
 * @property {Number} totalElements Total number of elements.
 * @property {Number} totalPages Total number of pages.
 * @property {Request} request Request binded to this pagination.
 * @property {Boolean} hasNext Tells if this paginating has more items to fetch.
 */

class Pagination {
  currentPage: number;

  elementsByPage: number;

  totalElements: number;

  totalPages: number;

  request: Request;

  /**
   * @method constructor
   * @private
   * @param {Number} startIndexElement Index of first element ot get.
   * @param {Number} elementsByPage Number of elements by page.
   * @param {Number} totalElements Total number of elements.
   * @param {Request} request Request binded to this pagination.
   * @example const pagination = new Pagination(0, 5, 100, request);
   */
  constructor(
    startIndexElement: number,
    elementsByPage: number,
    totalElements: number,
    request: Request
  ) {
    this.currentPage = Math.floor(startIndexElement / elementsByPage) + 1;
    this.elementsByPage = elementsByPage;
    this.totalElements = totalElements;
    this.totalPages = Math.ceil(totalElements / elementsByPage);
    this.request = request;
  }

  /**
   * @method previous
   * @public
   * @desc Execute binded request by setting its pagination parameters to the previous page.
   * @return {Promise} The promise handling the request.
   * @example
   * pagination.previous().then((newPageData) => {
   *    // ...
   * });
   */
  previous(): Promise<unknown> {
    return this.getPage(this.currentPage - 1);
  }

  /**
   * @method next
   * @public
   * @desc Execute binded request by setting its pagination parameters to the next page.
   * @return {Promise} The promise handling the request.
   * @example
   * pagination.next().then((newPageData) => {
   *    // ...
   * });
   */
  next(): Promise<unknown> {
    return this.getPage(this.currentPage + 1);
  }

  /**
   * @method getPage
   * @public
   * @desc Execute binded request by setting its pagination parameters to the given page.
   * @param {Number} pageNumber Page to get.
   * @return {Promise} The promise handling the request.
   * @example
   * pagination.getPage(4).then((pageData) => {
   *    // ...
   * });
   */
  getPage(pageNumber: number): Promise<unknown> {
    if (pageNumber < 1 || pageNumber > this.totalPages) {
      throw new RangeError(
        `Pagination::getPage: try to get page ${pageNumber} while minimum is 1 and maximum is ${this.totalPages}.`
      );
    }

    const start = this.elementsByPage * (pageNumber - 1);
    const end = start + this.elementsByPage - 1;

    return this.request
      .paginate(start, end)
      .execute()
      .then((data) => {
        this.currentPage = +pageNumber;

        return data;
      });
  }

  /**
   * @method isOnLastPage
   * @public
   * @desc Check if current page is the last one.
   * @return {Boolean} `true` if current page is the last one. `false` otherwise.
   * @example
   * const isOnLastPage = pagination.isOnLastPage();
   */
  isOnLastPage(): boolean {
    return this.currentPage === this.totalPages;
  }

  /**
   * @method isOnFirstPage
   * @public
   * @desc Check if current page is the first one.
   * @return {Boolean} `true` if current page is the first one. `false` otherwise.
   * @example
   * const isOnFirstPage = pagination.isOnFirstPage();
   */
  isOnFirstPage(): boolean {
    return this.currentPage === 1;
  }

  /**
   * @method serialize
   * @public
   * @desc Return a new JS object containing non-complex properties from this instance.
   * @return {Object} A JS object containing properties from this instance.
   * @example
   * const mySerializedPagination = pagination.serialize();
   */
  serialize(): {
    currentPage: number;
    elementsByPage: number;
    totalElements: number;
    totalPages: number;
  } {
    const { currentPage, elementsByPage, totalElements, totalPages } = this;

    return {
      currentPage,
      elementsByPage,
      totalElements,
      totalPages,
    };
  }
}

export default Pagination;
